import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { PublishTaskFormDocument, TaskFormDetailDocument } from 'generated/resources/graphql'

import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'

import { QuestionType } from '@factorialco/f0-react'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'

import Loading from 'modules/core/components/Loading'
import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities/provider'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import NewFormEditor from 'modules/forms/components/newFormEditor'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useResourceHeader } from 'modules/layout/components/ResponsivePage/hooks'
import { buildTaskFormRouteHeaderProps } from './taskFormHeader'

const PROCUREMENT_TASK_FORM_RESOURCE_TYPE = 'Procurement::TaskForm'

const ALLOWED_QUESTION_TYPES: QuestionType[] = [
  'text',
  'longText',
  'select',
  'multi-select',
  'numeric',
  'link',
  'date',
  'file',
  'checkbox',
]

export default function TaskFormDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { updateHeader } = useResourceHeader()
  const directUpload = useDirectUpload({ resourceType: PROCUREMENT_TASK_FORM_RESOURCE_TYPE })

  const useUpload = useCallback(
    () => ({
      upload: async (file: File) => {
        const result = await directUpload.upload(file)
        if (result.type === 'aborted') return { type: 'aborted' as const }
        return { type: 'success' as const, value: result.signedId }
      },
      cancelUpload: directUpload.cancelUpload,
      progress: directUpload.progress,
      status: directUpload.status,
    }),
    [directUpload]
  )

  const { data: taskFormData, loading } = useQuery(TaskFormDetailDocument, {
    variables: { id: id ?? '' },
    skip: !id,
  })

  useOneMutationSubscription('UpdateProcurementTaskForm', TaskFormDetailDocument, true)

  const [publishForm] = useMutation(PublishTaskFormDocument, {
    extractorFn: (data) => data.formsMutations.publishFormsForm,
  })

  const { openActivity } = useActivities()

  const taskForm = taskFormData?.procurement?.taskForm
  const latestForm = taskForm?.formsConnection?.edges?.[0]?.node
  const formId = latestForm?.id?.toString()

  useEffect(() => {
    if (!latestForm) return

    updateHeader(
      buildTaskFormRouteHeaderProps({
        id: latestForm.id.toString(),
        title: latestForm.title,
        description: latestForm.description,
        status: latestForm.status,
        updatedAt: latestForm.updatedAt,
        author: latestForm.author,
        elements: latestForm.elements,
      })
    )
  }, [latestForm, updateHeader])

  const handlePublish = async () => {
    if (!formId || !id) return

    const result = await publishForm({
      variables: {
        id: formId,
        resourceIds: [id],
        resourceType: PROCUREMENT_TASK_FORM_RESOURCE_TYPE,
      },
      refetchQueries: [TaskFormDetailDocument],
    })

    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
    })

    if (handled.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
    }
  }

  const handleActionClick = useCallback(
    async (key: string) => {
      if (key === 'publish') await handlePublish()
      if (key === 'preview')
        openActivity('forms.previewForm', {
          formId: formId ?? '',
          resourceType: PROCUREMENT_TASK_FORM_RESOURCE_TYPE,
        })
      if (key === 'duplicate')
        openActivity('procurement.duplicateTaskForm', {
          taskFormId: id ?? '',
          taskFormName: latestForm?.title ?? '',
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formId, id, openActivity, latestForm?.title]
  )

  if (loading) return <Loading expand />
  if (!taskForm)
    return (
      <OneEmptyState
        title={i18n.t('procurement.purchase_requests.settings.task_forms.detail.not_found')}
      />
    )
  if (!formId) return <OneEmptyState title={i18n.t('common.error')} />

  return (
    <ResponsivePage.Body variant="fullscreen" onActionClick={handleActionClick}>
      <NewFormEditor
        id={formId}
        resourceType={PROCUREMENT_TASK_FORM_RESOURCE_TYPE}
        allowedQuestionTypes={ALLOWED_QUESTION_TYPES}
        editable={taskForm.editable}
        useUpload={useUpload}
        requiredSkills={['procurementForms']}
        labels={{
          questionsLabel: i18n.t('procurement.task_forms.header.metadata.questions'),
          sectionsLabel: i18n.t('procurement.task_forms.header.metadata.sections'),
          saveSuccessMessage: i18n.t('procurement.task_forms.save_success'),
          saveErrorMessage: i18n.t('procurement.task_forms.save_error'),
          initialMessage: i18n.t('procurement.task_forms.one.chat_initial_message'),
        }}
      />
    </ResponsivePage.Body>
  )
}
